import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  ChatBubbleLeftIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { analyticsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const AnalyticsDashboard = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showDatePresets, setShowDatePresets] = useState(false);

  // Analytics data states
  const [overviewData, setOverviewData] = useState(null);
  const [trafficData, setTrafficData] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [chatbotData, setChatbotData] = useState(null);

  // Colors for charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Date presets
  const datePresets = [
    { label: 'Today', getValue: () => ({ startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }) },
    { label: 'Yesterday', getValue: () => {
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return { startDate: yesterday.toISOString().split('T')[0], endDate: yesterday.toISOString().split('T')[0] };
    }},
    { label: 'Last 7 Days', getValue: () => ({ startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }) },
    { label: 'Last 30 Days', getValue: () => ({ startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }) },
    { label: 'This Month', getValue: () => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: firstDay.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
    }},
    { label: 'Last Month', getValue: () => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: firstDay.toISOString().split('T')[0], endDate: lastDay.toISOString().split('T')[0] };
    }},
    { label: 'Last 90 Days', getValue: () => ({ startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }) },
  ];

  // Load data based on active tab
  useEffect(() => {
    loadData();
  }, [dateRange, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      switch (activeTab) {
        case 'overview':
          const overview = await analyticsAPI.getOverview(dateRange);
          setOverviewData(overview.data.data);
          break;
        case 'traffic':
          const traffic = await analyticsAPI.getTraffic(dateRange);
          setTrafficData(traffic.data.data);
          break;
        case 'conversions':
          const conversions = await analyticsAPI.getConversions(dateRange);
          setConversionData(conversions.data.data);
          break;
        case 'content':
          const content = await analyticsAPI.getContent(dateRange);
          setContentData(content.data.data);
          break;
        case 'chatbot':
          const chatbot = await analyticsAPI.getChatbot(dateRange);
          setChatbotData(chatbot.data.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handlePresetSelect = (preset) => {
    setDateRange(preset.getValue());
    setShowDatePresets(false);
    toast.success(`Date range set to: ${preset.label}`);
  };

  const handleExport = async (format) => {
    try {
      toast.loading('Preparing export...');
      
      if (format === 'pdf') {
        await exportToPDF();
      } else if (format === 'excel') {
        await exportToExcel();
      } else {
        // Original JSON/CSV export
        const response = await analyticsAPI.exportReport({
          ...dateRange,
          format,
          reportType: activeTab
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analytics-${activeTab}-${Date.now()}.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      toast.dismiss();
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.dismiss();
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(20);
    doc.text('LionTek Analytics Report', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(`Report Type: ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`, 20, 35);
    doc.text(`Period: ${dateRange.startDate} to ${dateRange.endDate}`, 20, 42);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 49);
    
    let yPos = 60;
    
    // Export based on active tab
    if (activeTab === 'overview' && overviewData) {
      doc.setFontSize(14);
      doc.text('Overview Metrics', 20, yPos);
      yPos += 10;
      
      const overviewRows = [
        ['Applications', overviewData.totalApplications || 0],
        ['Contact Messages', overviewData.totalMessages || 0],
        ['Newsletter Subscribers', overviewData.totalSubscribers || 0],
        ['Active Sessions', overviewData.activeSessions || 0],
        ['Blog Posts', overviewData.blogPostsPublished || 0],
        ['Active Jobs', overviewData.jobsActive || 0],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: overviewRows,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] }
      });
    } else if (activeTab === 'traffic' && trafficData) {
      doc.setFontSize(14);
      doc.text('Traffic Analytics', 20, yPos);
      yPos += 10;
      
      // Device breakdown
      if (trafficData.deviceBreakdown) {
        const deviceRows = trafficData.deviceBreakdown.map(d => [d.device_type, d.count]);
        autoTable(doc, {
          startY: yPos,
          head: [['Device Type', 'Sessions']],
          body: deviceRows,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246] }
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
      
      // Top browsers
      if (trafficData.topBrowsers) {
        doc.text('Top Browsers', 20, yPos);
        yPos += 5;
        const browserRows = trafficData.topBrowsers.map(b => [b.browser, b.count]);
        autoTable(doc, {
          startY: yPos,
          head: [['Browser', 'Sessions']],
          body: browserRows,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246] }
        });
      }
    } else if (activeTab === 'conversions' && conversionData) {
      doc.setFontSize(14);
      doc.text('Conversion Analytics', 20, yPos);
      yPos += 10;
      
      const conversionRows = [
        ['Total Applications', conversionData.totalApplications || 0],
        ['Total Messages', conversionData.totalMessages || 0],
        ['Total Subscribers', conversionData.totalSubscribers || 0],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: conversionRows,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] }
      });
      
      // Top performing jobs
      if (conversionData.topJobs && conversionData.topJobs.length > 0) {
        yPos = doc.lastAutoTable.finalY + 10;
        doc.text('Top Performing Jobs', 20, yPos);
        yPos += 5;
        const jobRows = conversionData.topJobs.map(j => [j.title, j.applicationCount]);
        autoTable(doc, {
          startY: yPos,
          head: [['Job Title', 'Applications']],
          body: jobRows,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246] }
        });
      }
    } else if (activeTab === 'content' && contentData) {
      doc.setFontSize(14);
      doc.text('Content Analytics', 20, yPos);
      yPos += 10;
      
      const contentRows = [
        ['Blog Posts Created', contentData.blogPostsCreated || 0],
        ['Blog Posts Published', contentData.blogPostsPublished || 0],
        ['Jobs Created', contentData.jobsCreated || 0],
        ['Active Jobs', contentData.jobsActive || 0],
        ['Portfolio Items', contentData.portfolioItemsAdded || 0],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: contentRows,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] }
      });
    } else if (activeTab === 'chatbot' && chatbotData) {
      doc.setFontSize(14);
      doc.text('Chatbot Analytics', 20, yPos);
      yPos += 10;
      
      const chatbotRows = [
        ['Total Conversations', chatbotData.totalConversations || 0],
        ['Resolved', chatbotData.resolvedConversations || 0],
        ['Unresolved', chatbotData.unresolvedConversations || 0],
        ['Resolution Rate', `${chatbotData.resolutionRate}%`],
        ['Average Rating', chatbotData.averageRating || 0],
        ['Average Confidence', `${chatbotData.averageConfidence}%`],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: chatbotRows,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] }
      });
      
      // Popular intents table
      if (chatbotData.popularIntents && chatbotData.popularIntents.length > 0) {
        yPos = doc.lastAutoTable.finalY + 10;
        doc.text('Popular Intents', 20, yPos);
        yPos += 5;
        const intentsRows = chatbotData.popularIntents.map(i => [i.intent, i.count]);
        autoTable(doc, {
          startY: yPos,
          head: [['Intent', 'Count']],
          body: intentsRows,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246] }
        });
      }
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    }
    
    doc.save(`analytics-${activeTab}-${Date.now()}.pdf`);
  };

  const exportToExcel = async () => {
    const workbook = XLSX.utils.book_new();
    
    // Export based on active tab
    if (activeTab === 'overview' && overviewData) {
      const data = [
        ['LionTek Analytics Report - Overview'],
        ['Period', `${dateRange.startDate} to ${dateRange.endDate}`],
        ['Generated', new Date().toLocaleString()],
        [],
        ['Metric', 'Value'],
        ['Applications', overviewData.totalApplications || 0],
        ['Contact Messages', overviewData.totalMessages || 0],
        ['Newsletter Subscribers', overviewData.totalSubscribers || 0],
        ['Active Sessions', overviewData.activeSessions || 0],
        ['Blog Posts Published', overviewData.blogPostsPublished || 0],
        ['Active Jobs', overviewData.jobsActive || 0],
      ];
      
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Overview');
    } else if (activeTab === 'traffic' && trafficData) {
      // Device breakdown sheet
      if (trafficData.deviceBreakdown) {
        const deviceData = [
          ['Device Type', 'Sessions'],
          ...trafficData.deviceBreakdown.map(d => [d.device_type, d.count])
        ];
        const deviceSheet = XLSX.utils.aoa_to_sheet(deviceData);
        XLSX.utils.book_append_sheet(workbook, deviceSheet, 'Devices');
      }
      
      // Browsers sheet
      if (trafficData.topBrowsers) {
        const browserData = [
          ['Browser', 'Sessions'],
          ...trafficData.topBrowsers.map(b => [b.browser, b.count])
        ];
        const browserSheet = XLSX.utils.aoa_to_sheet(browserData);
        XLSX.utils.book_append_sheet(workbook, browserSheet, 'Browsers');
      }
      
      // Locations sheet
      if (trafficData.topLocations) {
        const locationData = [
          ['Location', 'Sessions'],
          ...trafficData.topLocations.map(l => [l.location, l.count])
        ];
        const locationSheet = XLSX.utils.aoa_to_sheet(locationData);
        XLSX.utils.book_append_sheet(workbook, locationSheet, 'Locations');
      }
    } else if (activeTab === 'conversions' && conversionData) {
      const data = [
        ['LionTek Analytics Report - Conversions'],
        ['Period', `${dateRange.startDate} to ${dateRange.endDate}`],
        [],
        ['Metric', 'Value'],
        ['Total Applications', conversionData.totalApplications || 0],
        ['Total Messages', conversionData.totalMessages || 0],
        ['Total Subscribers', conversionData.totalSubscribers || 0],
      ];
      
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Conversions');
      
      // Top jobs sheet
      if (conversionData.topJobs && conversionData.topJobs.length > 0) {
        const jobData = [
          ['Job Title', 'Applications'],
          ...conversionData.topJobs.map(j => [j.title, j.applicationCount])
        ];
        const jobSheet = XLSX.utils.aoa_to_sheet(jobData);
        XLSX.utils.book_append_sheet(workbook, jobSheet, 'Top Jobs');
      }
    } else if (activeTab === 'content' && contentData) {
      const data = [
        ['LionTek Analytics Report - Content'],
        ['Period', `${dateRange.startDate} to ${dateRange.endDate}`],
        [],
        ['Metric', 'Value'],
        ['Blog Posts Created', contentData.blogPostsCreated || 0],
        ['Blog Posts Published', contentData.blogPostsPublished || 0],
        ['Jobs Created', contentData.jobsCreated || 0],
        ['Active Jobs', contentData.jobsActive || 0],
        ['Portfolio Items', contentData.portfolioItemsAdded || 0],
      ];
      
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Content');
    } else if (activeTab === 'chatbot' && chatbotData) {
      const data = [
        ['LionTek Analytics Report - Chatbot'],
        ['Period', `${dateRange.startDate} to ${dateRange.endDate}`],
        [],
        ['Metric', 'Value'],
        ['Total Conversations', chatbotData.totalConversations || 0],
        ['Resolved', chatbotData.resolvedConversations || 0],
        ['Unresolved', chatbotData.unresolvedConversations || 0],
        ['Resolution Rate', `${chatbotData.resolutionRate}%`],
        ['Average Rating', chatbotData.averageRating || 0],
        ['Average Confidence', `${chatbotData.averageConfidence}%`],
      ];
      
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Chatbot Summary');
      
      // Popular intents sheet
      if (chatbotData.popularIntents && chatbotData.popularIntents.length > 0) {
        const intentsData = [
          ['Intent', 'Count'],
          ...chatbotData.popularIntents.map(i => [i.intent, i.count])
        ];
        const intentsSheet = XLSX.utils.aoa_to_sheet(intentsData);
        XLSX.utils.book_append_sheet(workbook, intentsSheet, 'Popular Intents');
      }
    }
    
    XLSX.writeFile(workbook, `analytics-${activeTab}-${Date.now()}.xlsx`);
  };

  const renderGrowthIndicator = (growth) => {
    if (!growth && growth !== 0) return null;
    
    const isPositive = growth >= 0;
    return (
      <div className={`flex items-center text-sm font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? (
          <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
        ) : (
          <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
        )}
        <span>{Math.abs(growth)}%</span>
      </div>
    );
  };

  const renderStatCard = (title, value, subValue, Icon, color, growth) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <div className="mt-2 flex items-baseline">
            <p className="text-3xl font-semibold text-gray-900">{value}</p>
            {growth !== undefined && (
              <div className="ml-3">
                {renderGrowthIndicator(growth)}
              </div>
            )}
          </div>
          {subValue && (
            <p className="mt-1 text-sm text-gray-500">{subValue}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </div>
  );

  // Overview Tab Content
  const renderOverview = () => {
    if (!overviewData) return null;

    return (
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {renderStatCard(
            'Job Applications',
            overviewData.engagement.applications.total,
            `${overviewData.engagement.applications.avgPerJob} avg per job`,
            BriefcaseIcon,
            'bg-blue-500',
            overviewData.engagement.applications.growth
          )}
          {renderStatCard(
            'Contact Messages',
            overviewData.engagement.messages.total,
            `${overviewData.engagement.messages.responseRate}% response rate`,
            EnvelopeIcon,
            'bg-green-500',
            overviewData.engagement.messages.growth
          )}
          {renderStatCard(
            'Newsletter Signups',
            overviewData.engagement.subscribers.total,
            'New subscribers',
            UsersIcon,
            'bg-purple-500',
            overviewData.engagement.subscribers.growth
          )}
          {renderStatCard(
            'Active Sessions',
            overviewData.users.sessions.active,
            `${overviewData.users.sessions.activeRate}% of total`,
            ComputerDesktopIcon,
            'bg-yellow-500'
          )}
        </div>

        {/* Content Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Blog Posts</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Created</span>
                <span className="font-semibold">{overviewData.content.blogPosts.created}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Published</span>
                <span className="font-semibold text-green-600">{overviewData.content.blogPosts.published}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Growth</span>
                {renderGrowthIndicator(overviewData.content.blogPosts.growth)}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Jobs</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Created</span>
                <span className="font-semibold">{overviewData.content.jobs.created}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Active</span>
                <span className="font-semibold text-green-600">{overviewData.content.jobs.active}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Inactive Rate</span>
                <span className="text-red-600">{overviewData.content.jobs.inactiveRate}%</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversions</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Job App Rate</span>
                <span className="font-semibold text-blue-600">{overviewData.conversions.jobApplicationRate}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Message Response</span>
                <span className="font-semibold text-green-600">{overviewData.conversions.messageResponseRate}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Portfolio Items</span>
                <span className="font-semibold">{overviewData.content.portfolio.added}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Traffic Tab Content
  const renderTraffic = () => {
    if (!trafficData) return null;

    // Process device data for pie chart
    const deviceChartData = trafficData.devices.map((device, index) => ({
      name: device.device_type || 'Unknown',
      value: parseInt(device.count),
      color: COLORS[index % COLORS.length]
    }));

    return (
      <div className="space-y-6">
        {/* Device Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Breakdown</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={deviceChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {deviceChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Browsers</h3>
            <div className="space-y-3">
              {trafficData.browsers.map((browser, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-gray-600">{browser.browser || 'Unknown'}</span>
                  <span className="font-semibold">{browser.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Locations</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {trafficData.locations.slice(0, 5).map((location, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center">
                    <GlobeAltIcon className="h-4 w-4 mr-2" />
                    {location.location || 'Unknown'}
                  </span>
                  <span className="font-semibold">{location.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sessions Chart */}
        {trafficData.sessions.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Sessions Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trafficData.sessions}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="sessions" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Sessions" />
                <Area type="monotone" dataKey="uniqueUsers" stackId="2" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Unique Users" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  // Conversions Tab Content
  const renderConversions = () => {
    if (!conversionData) return null;

    // Process daily data for line chart
    const dailyData = [];
    const dates = new Set([
      ...conversionData.daily.applications.map(d => d.date),
      ...conversionData.daily.messages.map(d => d.date),
      ...conversionData.daily.subscribers.map(d => d.date)
    ]);

    dates.forEach(date => {
      const app = conversionData.daily.applications.find(d => d.date === date);
      const msg = conversionData.daily.messages.find(d => d.date === date);
      const sub = conversionData.daily.subscribers.find(d => d.date === date);

      dailyData.push({
        date,
        applications: parseInt(app?.count || 0),
        messages: parseInt(msg?.count || 0),
        subscribers: parseInt(sub?.count || 0)
      });
    });

    dailyData.sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
      <div className="space-y-6">
        {/* Daily Conversions Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Conversions</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="applications" stroke="#3b82f6" strokeWidth={2} name="Applications" />
              <Line type="monotone" dataKey="messages" stroke="#10b981" strokeWidth={2} name="Messages" />
              <Line type="monotone" dataKey="subscribers" stroke="#f59e0b" strokeWidth={2} name="Subscribers" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Breakdown Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Applications by Status</h3>
            <div className="space-y-3">
              {conversionData.breakdown.applicationsByStatus.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-gray-600 capitalize">{item.status}</span>
                  <span className="font-semibold">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Messages by Priority</h3>
            <div className="space-y-3">
              {conversionData.breakdown.messagesByPriority.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-gray-600 capitalize">{item.priority}</span>
                  <span className="font-semibold">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Performing Jobs */}
        {conversionData.topJobs.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Jobs</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applications</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {conversionData.topJobs.map((job, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{job.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.location}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">{job.applications}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Content Tab Content
  const renderContent = () => {
    if (!contentData) return null;

    return (
      <div className="space-y-6">
        {/* Blog Stats */}
        {contentData.blog.daily.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Blog Posts Published</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={contentData.blog.daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="published" fill="#10b981" name="Published" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Category Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Blog Categories</h3>
            <div className="space-y-3">
              {contentData.blog.byCategory.map((cat, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-gray-600">{cat.category || 'Uncategorized'}</span>
                  <span className="font-semibold">{cat.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Types</h3>
            <div className="space-y-3">
              {contentData.jobs.byType.map((type, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-gray-600">{type.employment_type || 'Unknown'}</span>
                  <span className="font-semibold">{type.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Jobs Posted Chart */}
        {contentData.jobs.daily.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Jobs Posted Over Time</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={contentData.jobs.daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="posted" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} name="Jobs Posted" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  const renderChatbot = () => {
    if (!chatbotData) return null;

    return (
      <div className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {renderStatCard('Total Conversations', chatbotData.totalConversations, null, ChatBubbleLeftIcon, 'blue', null)}
          {renderStatCard('Resolved', chatbotData.resolvedConversations, `${chatbotData.resolutionRate}% resolution rate`, ChatBubbleLeftIcon, 'green', null)}
          {renderStatCard('Average Rating', chatbotData.averageRating, `${chatbotData.ratedConversations} ratings`, ChatBubbleLeftIcon, 'yellow', null)}
          {renderStatCard('Avg Confidence', `${chatbotData.averageConfidence}%`, null, ChatBubbleLeftIcon, 'purple', null)}
        </div>

        {/* Daily Conversations Trend */}
        {chatbotData.dailyTrend && chatbotData.dailyTrend.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Conversations</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chatbotData.dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" name="Conversations" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Popular Intents and Rating Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Popular Intents */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Intents</h3>
            {chatbotData.popularIntents && chatbotData.popularIntents.length > 0 ? (
              <div className="space-y-3">
                {chatbotData.popularIntents.map((intent, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-600">{intent.intent || 'Unknown'}</span>
                    <span className="font-semibold">{intent.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No intent data available</p>
            )}
          </div>

          {/* Rating Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rating Distribution</h3>
            {chatbotData.ratingDistribution && chatbotData.ratingDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chatbotData.ratingDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="rating" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f59e0b" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500">No ratings yet</p>
            )}
          </div>
        </div>

        {/* Unresolved Conversations */}
        {chatbotData.unresolvedList && chatbotData.unresolvedList.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Unresolved Conversations ({chatbotData.unresolvedConversations} total)
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chatbotData.unresolvedList.map((conv, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-500">{conv.session_id?.substring(0, 8)}...</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{conv.user_message?.substring(0, 50)}...</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{conv.intent || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{conv.confidence ? `${conv.confidence}%` : 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(conv.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout title="Analytics & Reports">
      <div className="space-y-6">
        {/* Header with Controls */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Date Range Selector */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
              </div>
              
              {/* Date Presets Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowDatePresets(!showDatePresets)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Quick Select
                  <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showDatePresets && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      {datePresets.map((preset) => (
                        <button
                          key={preset.label}
                          onClick={() => handlePresetSelect(preset)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                PDF
              </button>
              <button
                onClick={() => handleExport('excel')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Excel
              </button>
              <button
                onClick={() => handleExport('json')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                JSON
              </button>
              <button
                onClick={() => handleExport('csv')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                CSV
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: ChartBarIcon },
                { id: 'traffic', label: 'Traffic', icon: GlobeAltIcon },
                { id: 'conversions', label: 'Conversions', icon: ArrowTrendingUpIcon },
                { id: 'content', label: 'Content', icon: BriefcaseIcon },
                { id: 'chatbot', label: 'Chatbot', icon: ChatBubbleLeftIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 ${
                    activeTab === tab.id
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'traffic' && renderTraffic()}
                {activeTab === 'conversions' && renderConversions()}
                {activeTab === 'content' && renderContent()}
                {activeTab === 'chatbot' && renderChatbot()}
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AnalyticsDashboard;
